import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from '@/hooks/use-toast';
import {
  FlaskConical,
  Plus,
  Upload,
  Calendar,
  Clock,
  FileText,
  Download,
  MapPin,
  RefreshCw,
  Eye,
} from 'lucide-react';
import LabInsightPopover from './intellihealth/LabInsightPopover';
import { Patient } from '@/lib/types';
import {
  getPatientOrders,
  getPatientResults,
  getRequisitionPDF,
  getResultPDF,
  LabOrder,
  LabResult,
  getOrderStatusLabel,
  getOrderStatusColor,
  getInterpretationLabel,
  getInterpretationColor,
  normalizeInterpretation,
} from '@/services/labService';
import { resolvePanelName } from '@/lib/labUtils';
import LabOrderSheet from '@/components/labs/LabOrderSheet';
import LabOrderDetailSheet from '@/components/labs/LabOrderDetailSheet';
import LabUploadDialog from '@/components/labs/LabUploadDialog';
import { useClinic } from '@/contexts/ClinicContext';

interface PatientLabsTabProps {
  patientId?: string;
  patientName?: string;
  patientEmail?: string;
  patientPhone?: string;
  patientAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  patientDob?: string;
  patientGender?: string;
  onPatientUpdated?: () => void;
}

const PatientLabsTab: React.FC<PatientLabsTabProps> = ({
  patientId,
  patientName,
  patientEmail,
  patientPhone,
  patientAddress,
  patientDob,
  patientGender,
  onPatientUpdated,
}) => {
  const [orders, setOrders] = useState<LabOrder[]>([]);
  const [results, setResults] = useState<LabResult[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingResults, setLoadingResults] = useState(true);
  const [orderSheetOpen, setOrderSheetOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [orderDetailSheetOpen, setOrderDetailSheetOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const { clinic } = useClinic();
  const patientStub = patientId ? ({ id: patientId } as Patient) : null;

  const fetchOrders = async () => {
    if (!patientId) return;
    try {
      setLoadingOrders(true);
      const data = await getPatientOrders(patientId);
      setOrders(data);
    } catch (error) {
      console.error('Error fetching lab orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchResults = async () => {
    if (!patientId) return;
    try {
      setLoadingResults(true);
      const data = await getPatientResults(patientId);
      setResults(data);
    } catch (error) {
      console.error('Error fetching lab results:', error);
    } finally {
      setLoadingResults(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchResults();
  }, [patientId]);

  const handleRefresh = () => {
    fetchOrders();
    fetchResults();
  };

  const handleDownloadRequisition = async (orderId: string) => {
    try {
      const blob = await getRequisitionPDF(orderId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `requisition-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: 'Downloaded', description: 'Requisition PDF downloaded' });
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'Failed to download requisition',
        variant: 'destructive',
      });
    }
  };

  const handleDownloadResult = async (resultId: string) => {
    try {
      const blob = await getResultPDF(resultId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lab-results-${resultId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: 'Downloaded', description: 'Results PDF downloaded' });
    } catch (error: any) {
      toast({
        title: 'Download Failed',
        description: error?.message || 'Failed to download results',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const newestOrderId = orders[0]?.id;

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (!patientId) {
    return (
      <div className="text-center py-12 text-gray-500">
        Patient ID not available
      </div>
    );
  }

  // Map results to order ID for displaying under each order
  const resultsByOrderId = React.useMemo(() => {
    const map = new Map<string, LabResult[]>();
    results.forEach((result) => {
      const orderId = result.order_id || (result.order as { id?: string })?.id;
      if (orderId) {
        const existing = map.get(orderId) || [];
        map.set(orderId, [...existing, result]);
      }
    });
    return map;
  }, [results]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <FlaskConical className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Lab Work</h3>
            <p className="text-sm text-gray-500">Orders and results</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          {/* <Button variant="outline" size="sm" onClick={() => setUploadDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-1" />
            Upload Labs
          </Button> */}
          <Button size="sm" onClick={() => setOrderSheetOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Order Labs
          </Button>
        </div>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Lab Orders
          </CardTitle>
          <CardDescription>Active and past lab orders</CardDescription>
        </CardHeader>
        <CardContent>
              {loadingOrders ? (
                <div className="space-y-3">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8">
                  <FlaskConical className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No lab orders yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => setOrderSheetOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Create First Order
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.map(order => {
                    const orderResults = resultsByOrderId.get(order.id) || [];
                    return (
                      <div
                        key={order.id}
                        className="border rounded-lg overflow-hidden hover:bg-gray-50/50 transition-colors"
                      >
                        <div className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  {resolvePanelName(order)}
                                </span>
                                <Badge className={getOrderStatusColor(order.status)}>
                                  {getOrderStatusLabel(order.status)}
                                </Badge>
                                {(order.guided_order_id || order.guidedOrder) && (
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                    Guided order
                                  </Badge>
                                )}
                                {order.id === newestOrderId && (
                                  <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                                    New
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3.5 w-3.5" />
                                  Ordered: {formatDateTime(order.created_at)}
                                </span>
                                {order.scheduled_at && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3.5 w-3.5" />
                                    Scheduled: {formatDateTime(order.scheduled_at)}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3.5 w-3.5" />
                                  {order.collection_method === 'walk_in' ? 'Walk-in' : 'At Home'}
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedOrderId(order.id);
                                  setOrderDetailSheetOpen(true);
                                }}
                                title="View order details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {order.requisition_url && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownloadRequisition(order.id)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        {orderResults.length > 0 && (
                          <div className="border-t bg-gray-50/80">
                            <Accordion type="single" collapsible className="w-full">
                              {orderResults.map((result) => {
                                const rawObservations = result.observations || [];
                                const getMarkerName = (obs: Record<string, unknown>): string =>
                                  (obs.loincDisplayName as string) ||
                                  (obs.providerMarkerName as string) ||
                                  (obs.markerName as string) ||
                                  (obs.marker_name as string) ||
                                  (obs.loinc_display as string) ||
                                  (obs.provider_marker_name as string) ||
                                  (obs.name as string) ||
                                  (obs.test_name as string) ||
                                  '';
                                const getValue = (obs: Record<string, unknown>): string => {
                                  const v = obs.value ?? obs.result ?? obs.resultValue ?? '';
                                  return v !== undefined && v !== null && v !== '' ? String(v) : '';
                                };
                                const getRefRange = (obs: Record<string, unknown>): string =>
                                  (obs.referenceRange as string) ||
                                  (obs.reference_range as string) ||
                                  (obs.range as string) ||
                                  (obs.referenceInterval as string) ||
                                  (obs.reference_interval as string) ||
                                  '';
                                const getInterpretation = (obs: Record<string, unknown>): string =>
                                  normalizeInterpretation(
                                    (obs.interpretation as string) || (obs.resultLevel as string) || ''
                                  );
                                const observations = rawObservations.filter(
                                  (obs: Record<string, unknown>) => getMarkerName(obs)
                                );
                                const abnormalCount = observations.filter(
                                  (o: Record<string, unknown>) => getInterpretation(o) !== 'N'
                                ).length;
                                return (
                                  <AccordionItem key={result.id} value={result.id} className="border-0">
                                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-100/50 [&[data-state=open]]:bg-gray-100/50">
                                      <div className="flex items-center justify-between w-full pr-2">
                                        <div className="flex items-center gap-3">
                                          <div className="flex items-center gap-2">
                                            <FlaskConical className="h-4 w-4 text-gray-600" />
                                            <span className="text-sm font-semibold text-gray-900">
                                              Results
                                            </span>
                                          </div>
                                          <Badge
                                            className={
                                              result.status === 'final'
                                                ? 'bg-green-100 text-green-800 border-green-200'
                                                : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                            }
                                          >
                                            {result.status}
                                          </Badge>
                                          {result.has_critical_values && (
                                            <Badge className="bg-red-100 text-red-800 border-red-200">
                                              Critical Values
                                            </Badge>
                                          )}
                                          {result.received_at && (
                                            <span className="text-sm text-gray-500">
                                              Received {formatDate(result.received_at)}
                                            </span>
                                          )}
                                          {abnormalCount > 0 && (
                                            <span className="text-red-600 text-sm">
                                              ({abnormalCount} abnormal)
                                            </span>
                                          )}
                                        </div>
                                        <span
                                          role="button"
                                          tabIndex={0}
                                          className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-200/50 cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDownloadResult(result.id);
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.stopPropagation();
                                              handleDownloadResult(result.id);
                                            }
                                          }}
                                        >
                                          <Download className="h-4 w-4" />
                                        </span>
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-0 pb-0 pt-0">
                                      {observations.length > 0 ? (
                                        <div className="bg-white">
                                          <div className="px-4 py-2 border-b">
                                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                              <span className="flex items-center gap-1">
                                                <Calendar className="h-3.5 w-3.5" />
                                                Received: {formatDate(result.received_at)}
                                              </span>
                                              <span>
                                                {observations.length} markers
                                                {abnormalCount > 0 && (
                                                  <span className="text-red-600 ml-1">
                                                    ({abnormalCount} abnormal)
                                                  </span>
                                                )}
                                              </span>
                                            </div>
                                          </div>
                                          <h4 className="text-sm font-medium text-gray-500 mb-2 px-4 pt-2">
                                            Completed lab results
                                          </h4>
                                          <div className="space-y-0 divide-y divide-gray-100">
                                            {observations.map((obs: Record<string, unknown>, idx: number) => {
                                              const markerName = getMarkerName(obs);
                                              const interpretation = getInterpretation(obs);
                                              const isAbnormal = interpretation !== 'N';
                                              const unit = String(
                                                obs.unit || obs.units || obs.unitOfMeasure || '',
                                              );
                                              const loinc =
                                                (obs.loincCode as string) ||
                                                (obs.loinc_code as string) ||
                                                undefined;
                                              return (
                                              <div
                                                key={idx}
                                                className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 transition-colors"
                                              >
                                                <div className="flex-1">
                                                  <div className="font-medium text-gray-900">
                                                    {markerName}
                                                  </div>
                                                  {result.received_at && (
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                      {formatDateTime(result.received_at)}
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="flex items-center gap-4">
                                                  <div className="text-right">
                                                    <div
                                                      className={`font-semibold text-base ${getInterpretationColor(
                                                        interpretation
                                                      )}`}
                                                    >
                                                      {getValue(obs)} {unit}
                                                    </div>
                                                    {getRefRange(obs) && (
                                                      <div className="text-xs text-gray-500 mt-0.5">
                                                        Ref: {getRefRange(obs)}
                                                      </div>
                                                    )}
                                                  </div>
                                                  <Badge
                                                    className={
                                                      !isAbnormal
                                                        ? 'bg-green-100 text-green-800 border-green-200'
                                                        : 'bg-red-100 text-red-800 border-red-200'
                                                    }
                                                  >
                                                    {getInterpretationLabel(interpretation)}
                                                  </Badge>
                                                  {isAbnormal && patientStub && (
                                                    <LabInsightPopover
                                                      patient={patientStub}
                                                      biomarker={{
                                                        name: markerName,
                                                        value: getValue(obs),
                                                        unit,
                                                        loinc,
                                                      }}
                                                    />
                                                  )}
                                                </div>
                                              </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      ) : (
                                        <p className="text-sm text-gray-400 py-4 text-center px-4">
                                          {rawObservations.length > 0 ? 'No valid marker data available' : 'No marker data available'}
                                        </p>
                                      )}
                                    </AccordionContent>
                                  </AccordionItem>
                                );
                              })}
                            </Accordion>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
        </CardContent>
      </Card>

      {/* Order Detail Sheet */}
      <LabOrderDetailSheet
        open={orderDetailSheetOpen}
        onOpenChange={setOrderDetailSheetOpen}
        orderId={selectedOrderId}
        onDownloadRequisition={handleDownloadRequisition}
        onDownloadResult={handleDownloadResult}
      />

      {/* Order Sheet */}
      <LabOrderSheet
        open={orderSheetOpen}
        onOpenChange={setOrderSheetOpen}
        patientId={patientId}
        patientName={patientName || 'Patient'}
        patientEmail={patientEmail}
        patientPhone={patientPhone}
        patientAddress={patientAddress}
        patientDob={patientDob}
        patientGender={patientGender}
        onOrderCreated={handleRefresh}
        onPatientUpdated={onPatientUpdated}
      />

      {/* Lab Upload Dialog */}
      {patientId && clinic?.id && (
        <LabUploadDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          patientId={patientId}
          clinicId={clinic.id}
          onUploadComplete={() => {
            handleRefresh();
          }}
        />
      )}
    </div>
  );
};

export default PatientLabsTab;













